/**
 * Brand-color theming for the public booking pages. Because the CTA buttons
 * read `var(--color-accent)` / `--color-accent-hover` / `--color-accent-fg`,
 * setting those three variables on a wrapper element re-themes every primary
 * button underneath with no per-component changes.
 */
function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const int = parseInt(h, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function toHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function isValidHex(hex: string | null | undefined): boolean {
  return !!hex && parseHex(hex) !== null;
}

/** Darken a hex by a fraction (for the button hover shade). */
export function darken(hex: string, amount = 0.14): string {
  const c = parseHex(hex);
  if (!c) return hex;
  return toHex(c.r * (1 - amount), c.g * (1 - amount), c.b * (1 - amount));
}

/** Pick readable text (near-black vs white) for a given background, by luminance. */
export function contrastText(hex: string): string {
  const c = parseHex(hex);
  if (!c) return '#ffffff';
  const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return lum > 0.62 ? '#16140f' : '#ffffff';
}

/** CSS custom-property overrides for a brand color, or {} when unset/invalid. */
export function brandVars(hex: string | null | undefined): Record<string, string> {
  if (!isValidHex(hex)) return {};
  const h = hex as string;
  return {
    '--color-accent': h,
    '--color-accent-hover': darken(h),
    '--color-accent-fg': contrastText(h),
  };
}
